import { parseDate } from '@/utils/helpers/dates';
import { type MaybeRefOrGetter, toValue } from '@/utils/vue';
import Decimal from 'decimal.js';
import { WritableComputedRef, computed } from 'vue';

export function useDecimal<P extends string, T extends null | { [K in P]?: string }>(
  target: MaybeRefOrGetter<T>,
  property: P,
  config: MaybeRefOrGetter<{
    defaultGetValue: number | Decimal | null;
    defaultSetModel: string | undefined;
  }>
): WritableComputedRef<Decimal | null> {
  return computed({
    get: () => {
      const value = toValue(target)?.[property];
      const defaultValue = toValue(config).defaultGetValue;
      return value || (!value && defaultValue !== null)
        ? new Decimal(+(value || defaultValue!))
        : null;
    },
    set: (value) => {
      const targetValue = toValue(target);
      if (targetValue === null) return;
      const number = value?.toNumber();
      targetValue[property] =
        value === null || number === undefined || isNaN(number)
          ? toValue(config).defaultSetModel
          : value.toString();
    },
  });
}

function toUTCISOString(date?: Date | null): string | null | undefined {
  return date === undefined || date === null
    ? date
    : new Date(date.setMinutes(-date.getTimezoneOffset())).toISOString();
}

export function useDate<P extends string, T extends null | { [K in P]?: string }>(
  target: MaybeRefOrGetter<T>,
  property: P,
  defaultGetValue: Date | null = null
): WritableComputedRef<Date | null> {
  return computed({
    get: () => {
      const targetValue = toValue(target);
      const value = targetValue?.[property] ?? defaultGetValue;
      return value === null
        ? null
        : typeof value === 'string'
          ? parseDate(value, undefined, 'UTC')
          : value;
    },
    set: (value) => {
      const targetValue = toValue(target);
      if (targetValue === null) return;
      const utcDate = toUTCISOString(value);
      targetValue[property] = utcDate?.slice(0, 10);
    },
  });
}
